import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { TESTIMONIALS } from "../data/site";

const AUTO_MS = 6000;
const PER_PAGE_DESKTOP = 3;

export default function TestimonialsCarousel() {
  const [start, setStart] = useState(0);
  const [paused, setPaused] = useState(false);
  const [pageSize, setPageSize] = useState(PER_PAGE_DESKTOP);

  useEffect(() => {
    const update = () => {
      if (window.innerWidth < 640) setPageSize(1);
      else if (window.innerWidth < 1024) setPageSize(2);
      else setPageSize(PER_PAGE_DESKTOP);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const total = TESTIMONIALS.length;
  const pages = Math.ceil(total / pageSize);
  const pageIdx = Math.floor(start / pageSize) % pages;

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setStart((s) => (s + pageSize) % total), AUTO_MS);
    return () => clearInterval(t);
  }, [paused, pageSize, total]);

  const visible = [];
  for (let i = 0; i < pageSize; i++) {
    visible.push(TESTIMONIALS[(start + i) % total]);
  }

  const prev = () => setStart((s) => (s - pageSize + total) % total);
  const next = () => setStart((s) => (s + pageSize) % total);

  // Extract just the person's name and role (without the company part after comma)
  const formatRole = (role) => {
    // Show only the job title, not the company name
    const parts = role.split(",");
    return parts[0].trim();
  };

  return (
    <div
      className="mt-14 relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="testimonials-carousel"
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={start + "-" + pageSize}
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -30 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {visible.map((t, i) => (
            <div
              key={`${start}-${i}`}
              className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm"
              data-testid={`testimonial-${(start + i) % total}`}
            >
              <Quote className="w-7 h-7 text-blue-600" />
              <p className="mt-4 text-slate-700 leading-relaxed">"{t.quote}"</p>
              <div className="flex gap-1 mt-4 text-yellow-500">
                {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-current" />)}
              </div>
              <div className="mt-5">
                <div className="font-semibold text-slate-900">{t.name}</div>
                <div className="text-xs text-slate-500">{formatRole(t.role)}</div>
              </div>
            </div>
          ))}
        </motion.div>
      </AnimatePresence>

      <div className="mt-8 flex items-center justify-center gap-4">
        <button
          onClick={prev}
          data-testid="testimonials-prev"
          aria-label="Previous testimonials"
          className="h-10 w-10 rounded-full border border-slate-200 hover:border-blue-300 hover:text-blue-600 grid place-items-center"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2" data-testid="testimonials-dots">
          {Array.from({ length: pages }).map((_, i) => (
            <button
              key={i}
              onClick={() => setStart(i * pageSize)}
              aria-label={`Go to page ${i + 1}`}
              className={`transition-all rounded-full ${
                i === pageIdx ? "w-8 h-2 bg-blue-600" : "w-2 h-2 bg-slate-300 hover:bg-slate-400"
              }`}
            />
          ))}
        </div>
        <button
          onClick={next}
          data-testid="testimonials-next"
          aria-label="Next testimonials"
          className="h-10 w-10 rounded-full border border-slate-200 hover:border-blue-300 hover:text-blue-600 grid place-items-center"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
